import React, { useState, useRef } from "react";
import MarketingShell from "./MarketingShell";
import Seo from "./Seo";
import { api } from "../lib/api";
import {
  Heart,
  Megaphone,
  Search,
  Share2,
  Handshake,
  Sparkles,
  Clock,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

/**
 * Volunteer With Us — public marketing page.
 *
 *   1. Hero  → CTA "Become a Volunteer" scrolls to the interest form.
 *   2. Four volunteer role cards (responsive 2-col / 1-col).
 *   3. Why Volunteer? — five reasons.
 *   4. Volunteer Interest Form — posts to /api/volunteer/apply.
 *
 * Form submissions are stored server-side AND emailed to
 * VOLUNTEER_INBOX_EMAIL (defaults to hello@sistersignal.org) via Resend.
 */

const ROLES = [
  {
    id: "community_outreach",
    icon: Megaphone,
    title: "Community Outreach Volunteer",
    mission:
      "Help us connect with domestic violence organizations, shelters, advocates, universities, and community partners.",
    responsibilities: [
      "Introduce Sister Signal to organizations",
      "Send emails and make connections",
      "Help expand awareness of our mission",
    ],
    skills: [
      "Friendly communicator",
      "Organized",
      "Comfortable sending emails",
      "Passion for helping women",
    ],
    note: "No previous nonprofit experience required.",
  },
  {
    id: "grant_research",
    icon: Search,
    title: "Grant Research Volunteer",
    mission: "Help us find funding opportunities so Sister Signal can continue growing.",
    responsibilities: [
      "Research grants",
      "Keep a spreadsheet of opportunities",
      "Help organize deadlines and requirements",
    ],
    skills: [
      "Detail oriented",
      "Good researcher",
      "Comfortable using Google Docs or spreadsheets",
    ],
    note: "No grant writing experience required.",
  },
  {
    id: "social_media",
    icon: Share2,
    title: "Social Media Volunteer",
    mission: "Help us educate and inspire more women through social media.",
    responsibilities: [
      "Schedule posts",
      "Help create captions",
      "Share educational content",
      "Engage with our online community",
    ],
    skills: ["Social media experience", "Creativity", "Writing", "Canva is a bonus"],
  },
  {
    id: "partnership",
    icon: Handshake,
    title: "Partnership Volunteer",
    mission:
      "Help build relationships with organizations and businesses that share our mission.",
    responsibilities: [
      "Reach out to nonprofits and community organizations",
      "Explore collaborations",
      "Help identify sponsorship opportunities",
      "Build long-term partnerships",
    ],
    skills: ["Networking", "Communication", "Organization", "Passion for community building"],
  },
];

const WHY = [
  "Help build technology that supports women.",
  "Join our founding volunteer team.",
  "Gain nonprofit experience.",
  "Build meaningful community connections.",
  "Help shape the future of Sister Signal.",
];

function RoleCard({ role }) {
  const Icon = role.icon;
  return (
    <article
      data-testid={`volunteer-role-${role.id}`}
      className="rounded-3xl bg-card border border-border p-7 flex flex-col"
    >
      <div className="w-12 h-12 rounded-2xl bg-brand-soft/60 flex items-center justify-center">
        <Icon className="w-6 h-6 text-brand" />
      </div>
      <h3 className="mt-4 font-serif text-2xl leading-tight">{role.title}</h3>
      <p className="mt-3 text-foreground/75 leading-relaxed text-sm">{role.mission}</p>

      <p className="mt-5 text-xs uppercase tracking-widest text-foreground/55">
        What you&rsquo;ll do
      </p>
      <ul className="mt-2 space-y-1.5 text-sm text-foreground/80">
        {role.responsibilities.map((r) => (
          <li key={r} className="flex gap-2">
            <span className="text-brand">•</span>
            <span>{r}</span>
          </li>
        ))}
      </ul>

      <div className="mt-5 inline-flex items-center gap-2 text-xs text-foreground/65">
        <Clock className="w-3.5 h-3.5" />
        <span>Remote · 2&ndash;5 hours per week</span>
      </div>

      <p className="mt-5 text-xs uppercase tracking-widest text-foreground/55">
        Helpful skills
      </p>
      <ul className="mt-2 flex flex-wrap gap-1.5">
        {role.skills.map((s) => (
          <li
            key={s}
            className="text-xs px-2.5 py-1 rounded-full bg-brand-soft/40 text-foreground/75 border border-brand/15"
          >
            {s}
          </li>
        ))}
      </ul>

      {role.note && (
        <p className="mt-4 text-xs text-foreground/55 italic">{role.note}</p>
      )}
    </article>
  );
}

const initialForm = {
  name: "",
  email: "",
  phone: "",
  city_state: "",
  roles: [],
  about: "",
  skills: "",
  hours_per_week: "",
  linkedin: "",
  another_way: "",
};

function VolunteerForm() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleRole = (id) =>
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(id)
        ? f.roles.filter((r) => r !== id)
        : [...f.roles, id],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!form.name.trim() || !form.email.trim()) {
      setError("Please add your name and email so we can get back to you.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/volunteer/apply", form);
      setSubmitted(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Something went wrong. Please try again or email us directly.";
      setError(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="volunteer-form-success"
        className="rounded-3xl bg-brand-soft/50 border border-brand/25 p-10 text-center"
      >
        <CheckCircle2 className="w-12 h-12 text-brand mx-auto" />
        <h3 className="mt-4 font-serif text-3xl leading-tight">Thank you.</h3>
        <p className="mt-3 text-foreground/80 leading-relaxed max-w-md mx-auto">
          Thank you for your interest in volunteering with Sister Signal! We&rsquo;ll be in touch
          soon.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="volunteer-form"
      className="rounded-3xl bg-card border border-border p-8 sm:p-10 space-y-6"
      noValidate
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <Field
          label="Name"
          required
          testId="volunteer-name"
          value={form.name}
          onChange={setField("name")}
          autoComplete="name"
        />
        <Field
          label="Email"
          required
          type="email"
          testId="volunteer-email"
          value={form.email}
          onChange={setField("email")}
          autoComplete="email"
        />
        <Field
          label="Phone (optional)"
          testId="volunteer-phone"
          value={form.phone}
          onChange={setField("phone")}
          autoComplete="tel"
        />
        <Field
          label="City / State (optional)"
          testId="volunteer-city"
          value={form.city_state}
          onChange={setField("city_state")}
        />
      </div>

      <fieldset>
        <legend className="text-sm font-medium text-foreground/85">
          Volunteer role(s) of interest
        </legend>
        <div className="mt-3 grid sm:grid-cols-2 gap-2.5">
          {ROLES.map((r) => (
            <label
              key={r.id}
              className="flex items-center gap-2 rounded-2xl bg-brand-soft/15 border border-border px-4 py-3 cursor-pointer hover:border-brand/40 transition-colors text-sm"
            >
              <input
                type="checkbox"
                data-testid={`volunteer-role-cb-${r.id}`}
                checked={form.roles.includes(r.id)}
                onChange={() => toggleRole(r.id)}
                className="accent-brand"
              />
              <span>{r.title.replace(" Volunteer", "")}</span>
            </label>
          ))}
          <label className="flex items-center gap-2 rounded-2xl bg-brand-soft/15 border border-border px-4 py-3 cursor-pointer hover:border-brand/40 transition-colors text-sm sm:col-span-2">
            <input
              type="checkbox"
              data-testid="volunteer-role-cb-another_way"
              checked={form.roles.includes("another_way")}
              onChange={() => toggleRole("another_way")}
              className="accent-brand"
            />
            <span>I&rsquo;d like to help in another way</span>
          </label>
        </div>
      </fieldset>

      <TextArea
        label="Tell us about yourself"
        testId="volunteer-about"
        value={form.about}
        onChange={setField("about")}
        placeholder="A few sentences about who you are and what draws you to Sister Signal."
        rows={4}
      />

      <TextArea
        label="Skills or experience"
        testId="volunteer-skills"
        value={form.skills}
        onChange={setField("skills")}
        placeholder="Anything you'd like us to know about your background."
        rows={3}
      />

      <div className="grid sm:grid-cols-2 gap-4">
        <Field
          label="Hours available each week"
          testId="volunteer-hours"
          value={form.hours_per_week}
          onChange={setField("hours_per_week")}
          placeholder="e.g. 2–5 hours"
        />
        <Field
          label="LinkedIn (optional)"
          testId="volunteer-linkedin"
          value={form.linkedin}
          onChange={setField("linkedin")}
          placeholder="https://linkedin.com/in/…"
          autoComplete="url"
        />
      </div>

      <TextArea
        label="I'd like to help in another way (optional)"
        testId="volunteer-another"
        value={form.another_way}
        onChange={setField("another_way")}
        placeholder="Tell us what feels right for you."
        rows={2}
      />

      {error && (
        <p
          data-testid="volunteer-form-error"
          className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-2xl px-4 py-3"
          role="alert"
        >
          {error}
        </p>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
        <p className="text-xs text-foreground/60 max-w-md leading-relaxed">
          By submitting, you agree we can email you about volunteering with Sister Signal. We will
          never share your information.
        </p>
        <button
          type="submit"
          data-testid="volunteer-submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand text-white hover:bg-brand/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed self-start sm:self-auto"
        >
          {submitting ? "Sending…" : "Submit interest"}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, type = "text", testId, value, onChange, placeholder, autoComplete }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-foreground/85">
        {label} {required && <span className="text-brand">*</span>}
      </span>
      <input
        type={type}
        data-testid={testId}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="mt-1.5 w-full rounded-2xl bg-card border border-border px-4 py-2.5 text-sm focus:outline-none focus:border-brand/60 focus:ring-2 focus:ring-brand/15 transition"
      />
    </label>
  );
}

function TextArea({ label, testId, value, onChange, placeholder, rows = 3 }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-foreground/85">{label}</span>
      <textarea
        data-testid={testId}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        className="mt-1.5 w-full rounded-2xl bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:border-brand/60 focus:ring-2 focus:ring-brand/15 transition resize-none"
      />
    </label>
  );
}

export default function Volunteer() {
  const formRef = useRef(null);
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <MarketingShell>
      <Seo
        title="Volunteer with Sister Signal"
        description="Help build Sister Signal. We're looking for compassionate volunteers — Community Outreach, Grant Research, Social Media, and Partnership roles. All remote and flexible."
        path="/volunteer"
      />

      {/* HERO ----------------------------------------------------------- */}
      <article
        className="max-w-3xl mx-auto px-6 sm:px-10 pt-20 pb-12"
        data-testid="page-volunteer"
      >
        <p className="font-script text-2xl text-brand">get involved</p>
        <h1 className="mt-2 font-serif text-5xl sm:text-6xl leading-tight">
          Help build Sister Signal.
        </h1>
        <p className="mt-6 text-xl text-foreground/80 leading-relaxed">
          Sister Signal is a nonprofit building technology to help women feel safer, more
          connected, and more supported. We&rsquo;re looking for compassionate volunteers who
          believe technology can make a real difference.
        </p>
        <p className="mt-4 text-foreground/75 leading-relaxed">
          Whether you have a few hours a month or a few hours each week, we&rsquo;d love to have
          you on our founding volunteer team. All volunteer roles are remote and flexible.
        </p>

        <button
          type="button"
          onClick={scrollToForm}
          data-testid="volunteer-hero-cta"
          className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand text-white hover:bg-brand/90 transition-all"
        >
          Become a Volunteer <ArrowRight className="w-4 h-4" />
        </button>
      </article>

      {/* ROLES ---------------------------------------------------------- */}
      <section
        className="bg-brand-soft/15 border-y border-brand/10"
        data-testid="volunteer-roles-section"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-16">
          <p className="font-script text-xl text-brand">our open roles</p>
          <h2 className="mt-1 font-serif text-4xl sm:text-5xl leading-tight">Four ways to help.</h2>
          <p className="mt-3 text-foreground/75 leading-relaxed max-w-2xl">
            Pick what feels like the best fit — or tell us in the form below if something else is
            calling you.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            {ROLES.map((r) => (
              <RoleCard key={r.id} role={r} />
            ))}
          </div>
        </div>
      </section>

      {/* WHY ------------------------------------------------------------ */}
      <section
        className="max-w-3xl mx-auto px-6 sm:px-10 py-20"
        data-testid="volunteer-why-section"
      >
        <p className="font-script text-xl text-brand">why join us</p>
        <h2 className="mt-1 font-serif text-4xl leading-tight">Why volunteer?</h2>
        <p className="mt-3 text-foreground/75 leading-relaxed">
          As a founding volunteer, you&rsquo;ll help shape a nonprofit from the ground up.
        </p>

        <ul className="mt-8 space-y-3">
          {WHY.map((line) => (
            <li
              key={line}
              className="flex items-start gap-3 rounded-2xl bg-card border border-border px-5 py-4"
            >
              <Heart className="w-5 h-5 text-brand mt-0.5 shrink-0" />
              <span className="text-foreground/85 leading-relaxed">{line}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* FORM ----------------------------------------------------------- */}
      <section
        ref={formRef}
        className="max-w-3xl mx-auto px-6 sm:px-10 pb-24"
        data-testid="volunteer-form-section"
        aria-labelledby="volunteer-form-heading"
      >
        <p className="font-script text-xl text-brand">tell us about you</p>
        <h2
          id="volunteer-form-heading"
          className="mt-1 font-serif text-4xl leading-tight"
        >
          Volunteer interest form.
        </h2>
        <p className="mt-3 text-foreground/75 leading-relaxed">
          A handful of details so we can find the right fit and reach back out within a few days.
        </p>

        <div className="mt-8">
          <VolunteerForm />
        </div>

        <p className="mt-8 text-sm text-foreground/65 leading-relaxed inline-flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-brand" />
          Prefer email? Reach us at{" "}
          <a
            href="mailto:hello@sistersignal.org"
            className="text-brand hover:underline underline-offset-2"
            data-testid="volunteer-email-fallback"
          >
            hello@sistersignal.org
          </a>
          .
        </p>
      </section>
    </MarketingShell>
  );
}
